import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  FiChevronLeft,
  FiChevronRight,
  FiHeart,
  FiMinus,
  FiPlus,
  FiShare2,
  FiX,
} from "react-icons/fi";
import productData from "../Datas/productData";
import { CartContext } from "./CartContext";
import siteConfig, { formatCurrency } from "../config/siteConfig";
import "../css/productdetails.scss";

const ProductDetail = () => {
  const { id } = useParams();
  const product = productData.find((item) => String(item.id) === String(id));
  const { addToCart, toggleWishlist, isWishlisted } = useContext(CartContext);
  const timerRef = useRef([]);
  const galleryTouchRef = useRef(null);
  const gallerySwipedRef = useRef(false);
  const galleryTriggerRef = useRef(null);
  const zoomCloseRef = useRef(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedSize, setSelectedSize] = useState(product?.size?.[0] || "");
  const [selectedColor, setSelectedColor] = useState(product?.colors?.[0] || "");
  const [quantity, setQuantity] = useState(1);
  const [addState, setAddState] = useState("idle");
  const [showAdded, setShowAdded] = useState(false);
  const [zoomOpen, setZoomOpen] = useState(false);
  const [shareMessage, setShareMessage] = useState("");

  const relatedProducts = useMemo(() => {
    if (!product) return [];
    const sameCategory = productData.filter(
      (item) => item.id !== product.id && item.category === product.category
    );
    const others = productData.filter(
      (item) => item.id !== product.id && item.category !== product.category
    );
    return [...sameCategory, ...others].slice(0, 4);
  }, [product]);

  useEffect(() => {
    if (!product) return;
    setSelectedImage(0);
    setSelectedSize(product.size?.[0] || "");
    setSelectedColor(product.colors?.[0] || "");
    setQuantity(1);
    setAddState("idle");
    setShowAdded(false);
  }, [product]);

  useEffect(() => () => {
    timerRef.current.forEach((timer) => window.clearTimeout(timer));
  }, []);

  useEffect(() => {
    if (!zoomOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    const galleryTrigger = galleryTriggerRef.current;
    document.body.style.overflow = "hidden";
    zoomCloseRef.current?.focus();
    const handleKey = (event) => {
      if (event.key === "Escape") setZoomOpen(false);
      if (event.key === "ArrowLeft") {
        setSelectedImage((current) => (current - 1 + product.images.length) % product.images.length);
      }
      if (event.key === "ArrowRight") {
        setSelectedImage((current) => (current + 1) % product.images.length);
      }
      if (event.key === "Tab") {
        const controls = Array.from(
          document.querySelectorAll(".product-zoom button:not([disabled])")
        );
        if (!controls.length) return;
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKey);
      galleryTrigger?.focus();
    };
  }, [product, zoomOpen]);

  useEffect(() => {
    if (!showAdded) return undefined;
    const dismissOnScroll = () => setShowAdded(false);
    window.addEventListener("scroll", dismissOnScroll, { passive: true, once: true });
    return () => window.removeEventListener("scroll", dismissOnScroll);
  }, [showAdded]);

  if (!product) {
    return (
      <main className="product-page product-page--not-found">
        <p>Product unavailable</p>
        <h1>We couldn&apos;t find that piece.</h1>
        <Link to="/shop">Return to the collection</Link>
      </main>
    );
  }

  const saved = isWishlisted(product.id);

  const startGallerySwipe = (event) => {
    const touch = event.touches[0];
    galleryTouchRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const endGallerySwipe = (event) => {
    if (!galleryTouchRef.current) return;
    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - galleryTouchRef.current.x;
    const deltaY = touch.clientY - galleryTouchRef.current.y;
    galleryTouchRef.current = null;
    if (Math.abs(deltaX) > 40 && Math.abs(deltaX) > Math.abs(deltaY)) {
      gallerySwipedRef.current = true;
      setSelectedImage((current) =>
        (current + (deltaX < 0 ? 1 : -1) + product.images.length) % product.images.length
      );
      const resetSwipeTimer = window.setTimeout(() => {
        gallerySwipedRef.current = false;
      }, 500);
      timerRef.current.push(resetSwipeTimer);
    }
  };

  const openGallery = () => {
    if (gallerySwipedRef.current) {
      gallerySwipedRef.current = false;
      return;
    }
    setZoomOpen(true);
  };

  const addProduct = () => {
    if (addState === "adding" || product.stock === 0) return;
    setAddState("adding");
    const addTimer = window.setTimeout(() => {
      addToCart(product.id, quantity, selectedSize, selectedColor);
      setAddState("added");
      setShowAdded(true);
      const resetTimer = window.setTimeout(() => setAddState("idle"), 3000);
      timerRef.current.push(resetTimer);
    }, 350);
    timerRef.current.push(addTimer);
  };

  const shareProduct = async () => {
    const shareData = {
      title: product.name,
      text: product.description,
      url: window.location.href,
    };
    try {
      if (navigator.share) await navigator.share(shareData);
      else if (navigator.clipboard) await navigator.clipboard.writeText(window.location.href);
      else throw new Error("Share unavailable");
      setShareMessage(navigator.share ? "Shared." : "Link copied.");
    } catch {
      setShareMessage("Copy the page address to share this product.");
    }
  };

  return (
    <main className="product-page" id="main-content">
      {showAdded && (
        <section className="product-added" aria-live="polite">
          <div className="ink-container">
            <button type="button" onClick={() => setShowAdded(false)} aria-label="Close added-to-cart message">
              <FiX aria-hidden="true" />
            </button>
            <p className="product-added__title">Just added</p>
            <img src={product.image} alt="" />
            <div>
              <strong>{product.name}</strong>
              <span>{selectedColor} · {selectedSize} · Quantity {quantity}</span>
              <small>{formatCurrency(product.price * quantity)}</small>
            </div>
            <div className="product-added__actions">
              <button type="button" onClick={() => setShowAdded(false)}>Keep shopping</button>
              <Link to="/cartpage">Cart</Link>
            </div>
          </div>
        </section>
      )}

      <div className="product-page__breadcrumbs ink-container">
        <Link to="/shop">Shop</Link>
        <span aria-hidden="true">/</span>
        <Link to={`/shop?subcategory=${encodeURIComponent(product.subcategory)}`}>
          {product.subcategory}
        </Link>
      </div>

      <div className="product-page__layout ink-container ink-container--mobile-bleed">
        <section className="product-gallery" aria-label={`${product.name} images`}>
          <button
            type="button"
            className="product-gallery__main"
            ref={galleryTriggerRef}
            onClick={openGallery}
            onTouchStart={startGallerySwipe}
            onTouchEnd={endGallerySwipe}
            aria-label={`Enlarge image ${selectedImage + 1} of ${product.images.length}`}
          >
            <img src={product.images[selectedImage]} alt={product.name} />
          </button>
          <div className="product-gallery__thumbs">
            {product.images.map((image, index) => (
              <button
                type="button"
                key={image}
                className={selectedImage === index ? "is-active" : ""}
                onClick={() => setSelectedImage(index)}
                aria-label={`View image ${index + 1}`}
                aria-current={selectedImage === index ? "true" : undefined}
              >
                <img src={image} alt="" />
              </button>
            ))}
          </div>
          <div className="product-gallery__progress" aria-hidden="true">
            {product.images.map((image, index) => (
              <span className={selectedImage === index ? "is-active" : ""} key={image} />
            ))}
          </div>
        </section>

        <section className="product-copy">
          <p className="product-copy__category">{product.subcategory}</p>
          <h1>{product.name}</h1>
          <p className="product-copy__price">{formatCurrency(product.price)}</p>
          <div className="product-copy__reviews" aria-label={`${product.rating} out of 5 stars from ${product.reviews} reviews`}>
            <span aria-hidden="true">★★★★★</span>
            <small>{product.rating} · {product.reviews} reviews</small>
          </div>
          <p className="product-copy__maker">
            By <Link to={`/shop?maker=${encodeURIComponent(product.brand)}`}>{product.brand}</Link>
          </p>

          <div className="product-copy__divider" />

          {product.colors?.length > 0 && (
            <label className="product-option">
              <span>Color</span>
              <select value={selectedColor} onChange={(event) => setSelectedColor(event.target.value)}>
                {product.colors.map((color) => <option value={color} key={color}>{color}</option>)}
              </select>
            </label>
          )}

          {product.size?.length > 0 && (
            <label className="product-option">
              <span>Option</span>
              <select value={selectedSize} onChange={(event) => setSelectedSize(event.target.value)}>
                {product.size.map((size) => <option value={size} key={size}>{size}</option>)}
              </select>
            </label>
          )}

          <div className="product-buy-row">
            <span className="product-quantity-label">Quantity</span>
            <div className="product-quantity" role="group" aria-label="Quantity">
              <button
                type="button"
                onClick={() => setQuantity((current) => Math.max(1, current - 1))}
                disabled={quantity <= 1}
                aria-label="Decrease quantity"
              >
                <FiMinus aria-hidden="true" />
              </button>
              <output>{quantity}</output>
              <button
                type="button"
                onClick={() => setQuantity((current) => Math.min(product.stock, current + 1))}
                disabled={quantity >= product.stock}
                aria-label="Increase quantity"
              >
                <FiPlus aria-hidden="true" />
              </button>
            </div>
            <button
              type="button"
              className="product-add-button"
              onClick={addProduct}
              disabled={product.stock === 0 || addState === "adding"}
            >
              {product.stock === 0
                ? "Out of stock"
                : addState === "adding"
                  ? "Adding…"
                  : addState === "added"
                    ? "Added to cart"
                    : "Add to cart"}
            </button>
          </div>

          <button
            type="button"
            className={`product-save ${saved ? "is-saved" : ""}`}
            onClick={() => toggleWishlist(product.id)}
            aria-pressed={saved}
          >
            <FiHeart aria-hidden="true" />
            {saved ? "Saved to your list" : "Save for later"}
          </button>

          {product.stock > 0 && product.stock < 20 && (
            <p className="product-copy__stock">Only {product.stock} left in stock.</p>
          )}

          <div className="product-description">
            <p>{product.description}</p>
            <h2>Details</h2>
            <dl>
              {Object.entries(product.attributes).map(([label, value]) => (
                <div key={label}><dt>{label}</dt><dd>{value}</dd></div>
              ))}
            </dl>
            <ul>{product.features.map((feature) => <li key={feature}>{feature}</li>)}</ul>
          </div>

          <div className="product-service-note">
            <strong>Shipping & returns</strong>
            <p>
              Tracked standard delivery is {formatCurrency(siteConfig.flatShippingRate)}.
              Eligible unused goods may be returned within 30 days.
            </p>
            <Link to="/shipping">View shipping information</Link>
          </div>

          <div className="product-share">
            <button type="button" onClick={shareProduct}>
              <FiShare2 aria-hidden="true" /> Share
            </button>
            <span role="status">{shareMessage}</span>
          </div>
        </section>
      </div>

      <section className="product-related ink-container" aria-labelledby="related-title">
        <h2 id="related-title">You may also like</h2>
        <div>
          {relatedProducts.map((item) => (
            <article key={item.id}>
              <Link to={`/shopDetails/${item.id}`}>
                <img src={item.image} alt={item.name} loading="lazy" />
                <span>{item.name}</span>
                <small>{formatCurrency(item.price)}</small>
              </Link>
            </article>
          ))}
        </div>
      </section>

      {zoomOpen && (
        <div className="product-zoom" role="dialog" aria-modal="true" aria-label={`${product.name} image viewer`}>
          <button
            type="button"
            className="product-zoom__close"
            ref={zoomCloseRef}
            onClick={() => setZoomOpen(false)}
            aria-label="Close image viewer"
          >
            <FiX aria-hidden="true" />
          </button>
          <button
            type="button"
            className="product-zoom__prev"
            onClick={() => setSelectedImage((current) => (current - 1 + product.images.length) % product.images.length)}
            aria-label="Previous image"
          >
            <FiChevronLeft aria-hidden="true" />
          </button>
          <img src={product.images[selectedImage]} alt={`${product.name}, view ${selectedImage + 1}`} />
          <button
            type="button"
            className="product-zoom__next"
            onClick={() => setSelectedImage((current) => (current + 1) % product.images.length)}
            aria-label="Next image"
          >
            <FiChevronRight aria-hidden="true" />
          </button>
        </div>
      )}
    </main>
  );
};

export default ProductDetail;
